import { useQuery } from '@tanstack/react-query';

import { InternalProperty, listMappedInternalProperties } from '~/services/AccommodationService';
import BedbankService from '~/services/BedbankService';
import ReservationService from '~/services/ReservationService';

export const internalPropertyRoomsQueryKeys = {
  all: ['accommodation', 'internal-property-rooms'] as const,
  lists: () => [...internalPropertyRoomsQueryKeys.all, 'list'] as const,
  list: (propertyId: string) => [...internalPropertyRoomsQueryKeys.lists(), propertyId] as const,
};

// Room mappings don't say which internal property a room belongs to, so the room ids are read from each
// internal property's source service (one request per internal property)
async function getInternalRoomIds({ source, sourceId }: InternalProperty, signal: AbortSignal): Promise<Array<string>> {
  if (source === 'reservation') {
    const { result } = await ReservationService.getProperty(sourceId, { signal });
    return result.room_types.map((roomType) => roomType.id);
  }
  const { result } = await BedbankService.getPropertyEdit(sourceId);
  return result.rooms.map((room) => room.id);
}

export function useGetInternalPropertyRooms({ propertyId }: { propertyId: string }) {
  return useQuery({
    queryKey: internalPropertyRoomsQueryKeys.list(propertyId),
    queryFn: async ({ signal }) => {
      const response = await listMappedInternalProperties({ propertyId, verificationStatuses: ['Approved'] });
      // An internal property whose rooms can't be fetched is left out rather than failing the whole list
      const settled = await Promise.allSettled(
        response.result.map(async ({ internalProperty }) => ({
          internalProperty,
          roomIds: new Set(await getInternalRoomIds(internalProperty, signal)),
        })),
      );
      return settled.flatMap((entry) => (entry.status === 'fulfilled' ? [entry.value] : []));
    },
    enabled: !!propertyId,
  });
}
